import i18n from '@/i18n';
import { taskAPI } from '@/utils/api';
import { sleepVisibilityAware } from '@/utils/pageVisibility';

const TERMINAL = new Set(['success', 'partial', 'failed', 'canceled', 'cancelled']);

function isFatalTaskPollError(error) {
  if (!error) return false;
  if (error.name === 'AbortError' || error.kind === 'aborted') return true;
  const status = Number(error.status);
  return status === 401 || status === 403 || status === 404;
}

function pickProgressRow(rows, taskId) {
  const id = String(taskId || '');
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return rows.find((row) => row && String(row.id) === id) || null;
}

function resultLooksEmpty(task) {
  const result = task?.result;
  if (!result || typeof result !== 'object') return true;
  return Object.keys(result).length === 0;
}

async function hydrateTerminalTask(taskId) {
  const res = await taskAPI.getById(taskId);
  let task = res?.task || res;
  if (resultLooksEmpty(task)) {
    await sleepVisibilityAware(400);
    const retry = await taskAPI.getById(taskId);
    task = retry?.task || retry || task;
  }
  return task;
}

/**
 * 工作流等场景：任务已入队后轮询至终态，由服务端 Worker 执行实际生成。
 * 进行中走 /tasks/progress（轻量 DTO）；终态再 getById 一次取全量 result。
 * 后台 Tab 自动降频，避免步骤忙等刷爆 API。
 */
export async function waitForFundingTaskComplete(
  taskId,
  { intervalMs = 2000, timeoutMs = 900000 } = {},
) {
  const id = String(taskId || '').trim();
  if (!id) throw new Error(i18n.t('workbench:ipeUtilError.taskIdInvalid'));
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    let status = '';
    try {
      const rows = await taskAPI.fetchProgressBatch([id]);
      const progress = pickProgressRow(rows, id);
      if (progress) {
        status = String(progress.status || '').toLowerCase();
      } else {
        // progress 未命中（暂未入库/鉴权跳过）时回退详情，避免工作流空转
        const res = await taskAPI.getById(id);
        const task = res?.task || res;
        status = String(task?.status || '').toLowerCase();
        if (TERMINAL.has(status)) {
          return resultLooksEmpty(task) ? hydrateTerminalTask(id) : task;
        }
      }
    } catch (error) {
      if (isFatalTaskPollError(error)) throw error;
      try {
        const task = await hydrateTerminalTask(id);
        status = String(task?.status || '').toLowerCase();
        if (TERMINAL.has(status)) {
          return task;
        }
      } catch (inner) {
        if (isFatalTaskPollError(inner)) throw inner;
        /* 瞬态错误：下一轮重试 */
      }
    }
    if (TERMINAL.has(status)) {
      try {
        return await hydrateTerminalTask(id);
      } catch (error) {
        if (isFatalTaskPollError(error)) throw error;
        /* 终态已确认，详情偶发失败则下一轮再取 */
      }
    }
    await sleepVisibilityAware(intervalMs);
  }
  throw new Error(i18n.t('workbench:ipeUtilError.taskTimeoutHistory'));
}

export function extractUrlsFromFundingTask(task) {
  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  if (Array.isArray(r.urls) && r.urls.length > 0) {
    return r.urls.map((u) => String(u || '').trim()).filter(Boolean);
  }
  if (Array.isArray(r.outputs)) {
    const fromOutputs = r.outputs
      .map((row) => String(row?.url || row?.outputUrl || row?.imageUrl || '').trim())
      .filter(Boolean);
    if (fromOutputs.length > 0) return fromOutputs;
  }
  if (r.localStampUrls && typeof r.localStampUrls === 'object') {
    const stampUrls = Object.values(r.localStampUrls)
      .map((u) => String(u || '').trim())
      .filter(Boolean);
    if (stampUrls.length > 0) return stampUrls;
  }
  if (r.localEnhanceUrls && typeof r.localEnhanceUrls === 'object') {
    const enhanceUrls = Object.values(r.localEnhanceUrls)
      .map((u) => String(u || '').trim())
      .filter(Boolean);
    if (enhanceUrls.length > 0) return enhanceUrls;
  }
  if (r.url) return [String(r.url).trim()].filter(Boolean);
  return [];
}

function pushUniqueUrl(list, url) {
  const next = String(url || '').trim();
  if (!next || list.includes(next)) return;
  list.push(next);
}

/** 视频任务 result → 成片 URL 列表（兼容 agent / dress / 历史字段） */
export function extractVideoUrlsFromFundingTask(task) {
  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  const urls = [];
  const scalarKeys = [
    'outputVideoUrl',
    'aiVideoUrl',
    'previewVideoUrl',
    'videoUrl',
    'generatedVideoUrl',
  ];
  scalarKeys.forEach((k) => pushUniqueUrl(urls, r[k]));

  const arrayCandidates = [r.outputVideoUrls, r.videos, r.videoOutputs, r.outputs];
  arrayCandidates.forEach((arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((entry) => {
      if (typeof entry === 'string') {
        pushUniqueUrl(urls, entry);
        return;
      }
      if (!entry || typeof entry !== 'object') return;
      pushUniqueUrl(
        urls,
        entry.url || entry.outputUrl || entry.videoUrl || entry.outputVideoUrl || entry.src,
      );
    });
  });
  return urls;
}
